// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import generateCustomQuotePdf from "@adl/App/Handlers/CustomQuote/GeneratePdf";
import saveCustomQuotePdf from "@adl/App/Handlers/CustomQuote/SavePdf";
import updatePdfSettings from "@adl/App/Handlers/CustomQuote/UpdatePdfSettings";
import uploadLogoToCusotmQuote from "@adl/App/Handlers/CustomQuote/UploadLogo";
import { CustomAppState } from "@adl/App/Store/Reducers";
import { getSettingsCollection } from "@adl/App/Store/Selectors/SettingsSelectors";
import Tooltip from "@adl/MobiusOverrides/Tooltip/Tooltip";
import { xScreen, xValidation } from "@adl/XDL/Utilities";
import { maxLengthAttr } from "@adl/XDL/Utilities/validation";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { CheckboxStyle } from "@insite/mobius/Checkbox";
import DatePicker, { DatePickerState } from "@insite/mobius/DatePicker";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal from "@insite/mobius/Modal";
import Radio from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useContext, useEffect, useRef, useState } from "react";
import * as React from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    customQuote: state.xMyCustomQuoteDetails.quote,
    pdfSettings: state.pdfSettings.parameters,
    countries: getCurrentCountries(state),
    loading: state.xMyCustomQuoteDetails.isLoading,
    allowSendPdfViaEmail: getSettingsCollection(state).customSettings?.enableEmailFeature,
});

const mapDispatchToProps = {
    updatePdfSettings,
    generateCustomQuotePdf,
    uploadLogoToCusotmQuote,
    saveCustomQuotePdf,
};

export interface MyListDetailsPDFStyles {
    accodrionTitle?: AccordionSectionPresentationProps & { initialExpanded: boolean };
    panelWrapper?: GridContainerProps;
    uploadLogo?: FileUploadPresentationProps;
    logoWrapper?: InjectableCss;
    informationFieldsWrapper?: GridItemProps;
    taxOptions?: GridItemProps;
    bottomSectionWrapper?: GridItemProps;
    formFields?: TextFieldPresentationProps;
    salesInputFieldsWrapper?: GridItemProps;
    printFormatGroup?: RadioGroupProps;
    emailPdfButton?: ButtonPresentationProps;
    savePdfButton?: ButtonPresentationProps;
    generatePdfButton?: ButtonPresentationProps;
    stateSelect?: SelectPresentationProps;
    countrySelect?: SelectPresentationProps;
    viewPdfWrapper?: InjectableCss;
    pdfButton?: ButtonPresentationProps;
    viewPdfText?: TypographyPresentationProps;
    sendToTextField?: TextFieldPresentationProps;
    datePickerGridItem?: GridItemProps;
    notesTextArea?: TextAreaProps;
    notesGridItem?: GridItemProps;
}

export const styles: MyListDetailsPDFStyles = {
    accodrionTitle: {
        initialExpanded: true,
        headerProps: {
            css: css`
                margin-top: 30px;
                button {
                    border: 0;
                    border-top: 2px solid ${getColor("common.ui5")};
                    background: transparent;
                    padding: 12px 0 !important;
                    &:focus {
                        border: 0;
                        border-top: 2px solid ${getColor("common.ui5")};
                    }
                    span {
                        font-size: 22px;
                        line-height: 22px;
                        letter-spacing: 0.22px;
                        font-weight: 500;
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                border: 0;
                padding: 30px 30px 20px;
                overflow: hidden;
                background: ${getColor("common.ui5")};
                ${CheckboxStyle} {
                    margin-bottom: 15px;
                    span[role="checkbox"] {
                        background: ${getColor("common.white")};
                    }
                }

                && input + label:before {
                    background: ${getColor("common.white")};
                }
                input:hover:checked:not(:disabled) + label::before {
                    background: ${getColor("common.white")};
                    border-color: ${getColor("common.white")};
                }

                @media (max-width: 481px) {
                    padding: 20px;
                }
            `,
        },
    },
    panelWrapper: {
        gap: 40,
    },
    informationFieldsWrapper: {
        width: [12, 6, 6, 4, 4],
        css: css`
            ${xScreen("lte", "sm")} {
                padding-top: 0;
            }
        `,
    },
    taxOptions: {
        width: [12, 6, 6, 4, 4],
        css: css`
            ${xScreen("lte", "sm")} {
                padding-top: 0;
            }
        `,
    },
    printFormatGroup: {
        css: css`
            legend {
                font-weight: 500;
            }
        `,
    },
    bottomSectionWrapper: {
        width: 12,
        css: css`
            justify-content: flex-end;
            position: relative;
            margin-top: 10px !important;
            &:before {
                content: " ";
                position: absolute;
                top: 0;
                width: calc(100% + 40px);
                left: -20px;
                height: 1px;
                background: ${getColor("common.ui2")};
            }
            @media (max-width: 480px) {
                flex-wrap: wrap;
            }
        `,
    },

    formFields: {
        cssOverrides: {
            formField: css`
                width: 100%;
                label {
                    font-weight: 500;
                }
            `,
            inputSelect: css`
                background: ${getColor("common.white")} !important;
            `,
        },
    },
    logoWrapper: {
        css: css`
            width: 100%;
        `,
    },
    emailPdfButton: {
        sizeVariant: "small",
        variant: "secondary",
        css: css`
            padding: 0 12px;
            margin-left: 40px;

            @media (max-width: 481px) {
                width: 100%;
                margin: 0;
                margin-bottom: 15px;
            }

            svg {
                color: ${getColor("common.ui2")};
            }
        `,
        icon: {
            src: "Mail",
            position: "left",
        },
    },
    savePdfButton: {
        sizeVariant: "small",
        css: css`
            padding: 0 12px;
            margin-left: 40px;

            @media (max-width: 481px) {
                width: 100%;
                margin: 0;
            }
        `,
        icon: {
            src: "Save",
            position: "left",
        },
    },
    generatePdfButton: {
        sizeVariant: "small",
        css: css`
            padding: 0 12px;
            margin-left: 40px;

            @media (max-width: 481px) {
                width: 100%;
                margin: 0;
                margin-bottom: 15px;
            }
        `,
        icon: {
            src: "File",
            position: "left",
        },
    },
    viewPdfWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
        `,
    },
    viewPdfText: {
        size: "18px",
        lineHeight: "22px",
        css: css`
            display: block;
            width: 100%;
            margin-bottom: 15px;
        `,
    },
    pdfButton: {
        css: css`
            float: right;
            margin-right: 0;
            margin-left: auto;
        `,
    },
    sendToTextField: {
        css: css`
            margin-bottom: 20px;
        `,
    },
    datePickerGridItem: {
        width: 12,
        css: css`
            padding-top: 5px;
            && > div,
            && > div > div {
                width: 100%;
            }
        `,
    },
    notesGridItem: {
        width: [12, 6, 6, 4, 4],
        css: css`
            padding-top: 0;
        `,
    },
    notesTextArea: {
        hintProps: {
            size: 10,
        },
        css: css`
            ${xScreen("lte", "sm")} {
                height: 110px;
            }
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const MyCustomQuoteDetailsPdfSettings: React.FC<Props> = ({
    customQuote,
    pdfSettings,
    updatePdfSettings,
    generateCustomQuotePdf,
    countries,
    loading,
    uploadLogoToCusotmQuote,
    allowSendPdfViaEmail,
    saveCustomQuotePdf,
}) => {
    if (!customQuote || !countries || loading) {
        return null;
    }

    const toasterContext = useContext(ToasterContext);
    const [clientName, setClientName] = useState(pdfSettings.clientName);
    const [clientNameError, setClientNameError] = useState("");
    const [logoFileName, setLogoFileName] = useState(pdfSettings.logoFileName);
    const [file, setFile] = useState<any>(null);
    const [website, setWebsite] = useState(pdfSettings.website);
    const [phone, setPhone] = useState(pdfSettings.phoneNumber);
    const [phoneError, setPhoneError] = useState("");
    const [email, setEmail] = useState(pdfSettings.email);
    const [emailError, setEmailError] = useState("");
    const [country, setCountry] = useState<CountryModel>();
    const [streetAddress1, setStreetAddress1] = useState(pdfSettings.streetAddress1);
    const [streetAddress2, setStreetAddress2] = useState(pdfSettings.streetAddress2);
    const [city, setCity] = useState(pdfSettings.city);
    const [province, setProvince] = useState(pdfSettings.province);
    const [postalCode, setPostalCode] = useState(pdfSettings.postalCode);
    const [freightCharges, setFreightCharges] = useState(pdfSettings.freightCharges || 0.0);
    const [labourCharges, setLabourCharges] = useState(pdfSettings.labourCharges || 0.0);
    const [shopSupplies, setShopSupplies] = useState(pdfSettings.shopSupplies || 0.0);
    const [displayPrice, setDisplayPrice] = useState(
        pdfSettings.priceDisplayOption || customQuote.priceDisplayTypes[0],
    );
    const [expirationDate, setExpirationDate] = useState(pdfSettings.expirationDate);
    // XNG-Change: set selected country or the only country
    const [selectedCountry, setSelectedCountry] = useState<CountryModel>(country || countries[0]);
    const [states, setStates] = useState<StateModel[]>(selectedCountry?.states || []);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [pdfLink, setPdfLink] = useState("");
    const [isPdfReady, setIsPdfReady] = useState(false);
    const [sendByEmail, setSendByEmail] = useState(false);
    const [sendToEmail, setSendToEmail] = useState("");
    const [sendToEmailError, setSendToEmailError] = useState("");
    const [generalNote, setGeneralNote] = useState(pdfSettings.generalNote || "");
    const fileUploadRef = useRef({ value: "" } as HTMLInputElement);

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPhone(e.target.value);
    };

    const handleClientNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setClientName(e.target.value);
    };

    const handleAddress1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
        setStreetAddress1(e.target.value);
    };

    const handleAddress2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
        setStreetAddress2(e.target.value);
    };

    const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCity(e.target.value);
    };

    const handleStateChange = (e: React.FormEvent<HTMLSelectElement>) => {
        setProvince(e.currentTarget.value);
    };

    const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPostalCode(e.target.value);
    };

    const handleFrieghtChargesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFreightCharges(parseFloat(e.target.value || "0"));
    };

    const handleLabourChargesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLabourCharges(parseFloat(e.target.value || "0"));
    };

    const handleShopSuppliesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setShopSupplies(parseFloat(e.target.value || "0"));
    };

    const handlePriceDisplayChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as "ShowProductPrices" | "HideProductPrices";
        setDisplayPrice(value);
    };

    const expirationDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        setExpirationDate(selectedDay?.toDateString());
    };

    const handleWebsiteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setWebsite(e.target.value);
    };
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
    };
    const handleSendToEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSendToEmail(e.target.value);
    };

    const handleCountryChange = (event: React.FormEvent<HTMLSelectElement>) => {
        const country = countries?.find(c => c.id === event.currentTarget.value);
        if (country) {
            setSelectedCountry(country);
            setStates(country.states || []);
        }
    };

    const saveAndGeneratePdf = () => {
        if (!validateFields()) {
            return;
        }
        setIsLoading(true);
        generateCustomQuotePdf({
            ...pdfSettings,
            expirationDate,
            sendPdfByEmail: false,
            onSuccess(result: any) {
                setPdfLink(result);
                setIsLoading(false);
                setIsPdfReady(true);
            },
            onError(error: any) {
                setIsLoading(false);
                toasterContext.addToast({ body: error, messageType: "danger" });
            },
        } as any);
    };

    const sendPdfByEmail = () => {
        if (!xValidation.email(sendToEmail, true)) {
            setSendToEmailError(translate("Invalid Email"));
            return;
        }
        setIsLoading(true);
        setSendToEmailError("");
        generateCustomQuotePdf({
            ...pdfSettings,
            expirationDate,
            sendPdfByEmail: true,
            pdfDestinationEmailAddress: sendToEmail,
            onSuccess(result: any) {
                setIsLoading(false);
                setSendByEmail(false);
                setSendToEmail("");
                toasterContext.addToast({ body: translate("Email Sent"), messageType: "success" });
            },
            onError(error: any) {
                setIsLoading(false);
                toasterContext.addToast({ body: error, messageType: "danger" });
            },
        } as any);
    };

    const savePdf = () => {
        if (!checkIfDataChanged()) {
            return;
        }
        setIsSaving(true);
        saveCustomQuotePdf({
            ...pdfSettings,
            expirationDate,
            sendPdfByEmail: false,
            onSuccess() {
                setIsSaving(false);
                toasterContext.addToast({ body: translate("Saved"), messageType: "success" });
            },
            onError(error: any) {
                setIsSaving(false);
                toasterContext.addToast({ body: error, messageType: "danger" });
            },
        } as any);
    };

    const checkIfDataChanged = (): boolean => {
        if (
            pdfSettings.website !== customQuote.website ||
            pdfSettings.clientName !== customQuote.clientName ||
            pdfSettings.phoneNumber !== customQuote.phoneNumber ||
            pdfSettings.streetAddress1 !== customQuote.streetAddress1 ||
            pdfSettings.streetAddress2 !== customQuote.streetAddress2 ||
            pdfSettings.city !== customQuote.city ||
            pdfSettings.email !== customQuote.email ||
            pdfSettings.province !== customQuote.province ||
            pdfSettings.postalCode !== customQuote.postalCode ||
            pdfSettings.freightCharges !== customQuote.freightCharges ||
            pdfSettings.shopSupplies !== customQuote.shopSupplies ||
            pdfSettings.labourCharges !== customQuote.labourCharges ||
            pdfSettings.priceDisplayOption !== customQuote.priceDisplayType ||
            pdfSettings.expirationDate !== customQuote.expiryDate ||
            pdfSettings.logoFileName !== customQuote.logoFileName ||
            pdfSettings.generalNote !== customQuote.generalNote ||
            expirationDate !== customQuote.expiryDate
        ) {
            return true;
        }
        return false;
    };

    const validateFields = () => {
        let canSubmit = true;
        if (!email) {
            setEmailError(translate("Email is Required"));
            canSubmit = false;
        } else if (!xValidation.email(email, true)) {
            setEmailError(translate("Invalid Email"));
            canSubmit = false;
        } else {
            setEmailError("");
        }
        if (!phone) {
            setPhoneError(translate("Phone is Required"));
            canSubmit = false;
        } else if (!xValidation.phone(phone, true)) {
            setPhoneError(translate("Invalid Phone Number"));
            canSubmit = false;
        } else {
            setPhoneError("");
        }
        if (!clientName) {
            setClientNameError(translate("Client Name is Required"));
            canSubmit = false;
        } else {
            setClientNameError("");
        }
        return canSubmit;
    };

    const buildFormDataForTax = (uploadedFile: any) => {
        const formData = new FormData();
        formData.append(`file`, uploadedFile, uploadedFile.name || "");

        formData.append(`quoteId`, customQuote.id!);

        return formData;
    };

    const fileChangeHandler = (event: React.ChangeEvent<any>) => {
        const uploadedFile = event.target.files[0];
        setFile(uploadedFile);

        if (uploadedFile) {
            setLogoFileName(uploadedFile.name);
        }
        if (uploadedFile) {
            const formData = buildFormDataForTax(uploadedFile);
            setIsLoading(true);
            uploadLogoToCusotmQuote({
                formData,
                customQuoteId: customQuote.id,
                onError: error => {
                    setIsLoading(false);
                    toasterContext.addToast({ body: error, messageType: "danger" });
                },
                onSuccess: result => {
                    setIsLoading(false);
                    toasterContext.addToast({ body: translate("Logo Uploaded"), messageType: "success" });
                },
            });
        }
    };

    const notesChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setGeneralNote(
            event.target.value.length > maxLengthAttr.quoteNotes
                ? event.target.value.slice(0, maxLengthAttr.quoteNotes)
                : event.target.value,
        );
    };

    const onBlurUpdatePdfSettings = () => {
        updatePdfSettings({
            parameters: {
                website,
                clientName,
                phoneNumber: phone,
                streetAddress1,
                streetAddress2,
                city,
                email,
                province,
                postalCode,
                freightCharges,
                labourCharges,
                priceDisplayOption: displayPrice,
                expirationDate,
                quoteName: customQuote?.name,
                id: customQuote?.id,
                logoFileName,
                shopSupplies,
                generalNote,
            },
        });
    };

    const formatType = (type: string) => {
        return type.replace(/([A-Z])/g, ' $1').trim();
    };

    return (
        <>
            <Accordion
                headingLevel={1}
                css={css`
                    margin-bottom: 50px;
                    @media print {
                        display: none;
                    }
                `}
            >
                <ManagedAccordionSection {...styles.accodrionTitle} title={translate("PDF Display Settings")}>
                    <LoadingOverlay loading={isLoading}>
                        <GridContainer {...styles.panelWrapper}>
                            <GridItem {...styles.informationFieldsWrapper}>
                                <GridContainer gap={20}>
                                    <GridItem width={12}>
                                        <StyledWrapper {...styles.logoWrapper}>
                                            <FileUpload
                                                {...styles.uploadLogo}
                                                hideButton={true}
                                                inputRef={fileUploadRef}
                                                fileName={logoFileName}
                                                placeholder={translate("Upload .JPG, .PNG, .BMP and .GIF")}
                                                accept=".jpg,.jpeg,.png,.bmp,.gif"
                                                label={
                                                    <>
                                                        <Typography>{translate("Logo")}</Typography>
                                                        <Tooltip placement="top" text={customQuote.logoTooltipText} />
                                                    </>
                                                }
                                                labelPosition="top"
                                                onFileChange={fileChangeHandler}
                                                uid="uploadLogoInput"
                                                onBlur={onBlurUpdatePdfSettings}
                                            />
                                        </StyledWrapper>
                                    </GridItem>
                                    <GridItem width={12}>
                                        <TextField
                                            {...styles.formFields}
                                            type="text"
                                            value={website}
                                            onChange={handleWebsiteChange}
                                            maxLength={120}
                                            label={translate("My Website")}
                                            onBlur={onBlurUpdatePdfSettings}
                                        />
                                    </GridItem>

                                    <GridItem width={12}>
                                        <TextField
                                            {...styles.formFields}
                                            type="text"
                                            required
                                            error={clientNameError}
                                            value={clientName}
                                            onChange={handleClientNameChange}
                                            label={translate("Client Name")}
                                            onBlur={onBlurUpdatePdfSettings}
                                        />
                                    </GridItem>
                                    <GridItem width={12}>
                                        <TextField
                                            {...styles.formFields}
                                            type="email"
                                            value={email}
                                            required
                                            error={emailError}
                                            maxLength={128}
                                            onChange={handleEmailChange}
                                            label={translate("Email")}
                                            onBlur={onBlurUpdatePdfSettings}
                                        />
                                    </GridItem>
                                    <GridItem width={12}>
                                        <TextField
                                            {...styles.formFields}
                                            type="phone"
                                            required
                                            error={phoneError}
                                            value={phone}
                                            onChange={handlePhoneChange}
                                            label={translate("PhoneNumber")}
                                            onBlur={onBlurUpdatePdfSettings}
                                        />
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...styles.taxOptions}>
                                <GridContainer gap={20}>
                                    <GridItem width={12}>
                                        <TextField
                                            {...styles.formFields}
                                            type="text"
                                            value={streetAddress1}
                                            maxLength={50}
                                            onChange={handleAddress1Change}
                                            label={translate("Address 1")}
                                            onBlur={onBlurUpdatePdfSettings}
                                        />
                                    </GridItem>
                                    <GridItem width={12}>
                                        <TextField
                                            {...styles.formFields}
                                            className="address2"
                                            type="text"
                                            value={streetAddress2}
                                            maxLength={50}
                                            onChange={handleAddress2Change}
                                            label={translate("Address 2")}
                                            onBlur={onBlurUpdatePdfSettings}
                                        />
                                    </GridItem>
                                    <GridItem width={12}>
                                        <TextField
                                            {...styles.formFields}
                                            type="text"
                                            value={city}
                                            maxLength={50}
                                            onChange={handleCityChange}
                                            label={translate("City")}
                                            onBlur={onBlurUpdatePdfSettings}
                                        />
                                    </GridItem>
                                    <GridItem width={6}>
                                        <Select
                                            {...styles.countrySelect}
                                            label={translate("Country")}
                                            value={selectedCountry?.id}
                                            onChange={handleCountryChange}
                                            disabled={countries.length === 1}
                                            onBlur={onBlurUpdatePdfSettings}
                                        >
                                            <option value="">{translate("Select Country")}</option>
                                            {countries.map(co => (
                                                <option key={co.id} value={co.id}>
                                                    {co.name}
                                                </option>
                                            ))}
                                        </Select>
                                    </GridItem>
                                    {states && (
                                        <GridItem width={6}>
                                            <Select
                                                {...styles.stateSelect}
                                                label={translate("State")}
                                                value={province}
                                                onChange={handleStateChange}
                                                onBlur={onBlurUpdatePdfSettings}
                                            >
                                                <option value="">{translate("Select State")}</option>
                                                {states.map(st => (
                                                    <option key={st.id} value={st.abbreviation}>
                                                        {st.name}
                                                    </option>
                                                ))}
                                            </Select>
                                        </GridItem>
                                    )}
                                    <GridItem width={12}>
                                        <TextField
                                            {...styles.formFields}
                                            name="postalCode"
                                            label={translate("Postal Code")}
                                            value={postalCode}
                                            onBlur={onBlurUpdatePdfSettings}
                                            onChange={handlePostalCodeChange}
                                            data-test-selector="customerAddressForm_postalCode"
                                        />
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...styles.taxOptions}>
                                <GridContainer gap={20}>
                                    <GridItem width={12}>
                                        <TextField
                                            {...styles.formFields}
                                            type="number"
                                            value={freightCharges}
                                            min={0}
                                            onChange={handleFrieghtChargesChange}
                                            maxLength={120}
                                            onBlur={onBlurUpdatePdfSettings}
                                            label={translate("Freight Charges")}
                                        />
                                    </GridItem>
                                    <GridItem width={12}>
                                        <TextField
                                            {...styles.formFields}
                                            type="number"
                                            min={0}
                                            value={labourCharges}
                                            onChange={handleLabourChargesChange}
                                            maxLength={120}
                                            onBlur={onBlurUpdatePdfSettings}
                                            label={translate("Labour Charges")}
                                        />
                                    </GridItem>
                                    <GridItem width={12}>
                                        <TextField
                                            {...styles.formFields}
                                            type="number"
                                            min={0}
                                            value={shopSupplies}
                                            onChange={handleShopSuppliesChange}
                                            maxLength={120}
                                            onBlur={onBlurUpdatePdfSettings}
                                            label={translate("Shop Supplies")}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.datePickerGridItem}>
                                        <DatePicker
                                            data-test-selector="customQuoteDetails_expirationDate"
                                            label={translate("Quote Expiration Date")}
                                            selectedDay={
                                                expirationDate ? new Date(expirationDate) : (expirationDate as any)
                                            }
                                            dateTimePickerProps={{
                                                minDate: new Date(),
                                                maxDate: undefined,
                                            }}
                                            onDayChange={expirationDateChangeHandler}
                                        />
                                    </GridItem>
                                    <GridItem width={12}>
                                        <RadioGroup
                                            label={translate("Price Display Options")}
                                            {...styles.printFormatGroup}
                                            value={displayPrice}
                                            horizontal
                                            onChangeHandler={handlePriceDisplayChange}
                                            onBlur={onBlurUpdatePdfSettings}
                                        >
                                            {customQuote.priceDisplayTypes.map(type => {
                                                return (
                                                    <Radio key={type} value={type}>{formatType(type)}</Radio>
                                                )
                                            })}
                                        </RadioGroup>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem {...styles.notesGridItem}>
                                <TextArea
                                    {...styles.notesTextArea}
                                    hint={`${500 - generalNote.length} ${translate("characters left")}`}
                                    value={generalNote}
                                    onChange={notesChangeHandler}
                                    maxLength={maxLengthAttr.quoteNotes}
                                    label={translate("Notes:")}
                                    onBlur={onBlurUpdatePdfSettings}
                                />
                            </GridItem>
                            <GridItem {...styles.bottomSectionWrapper}>
                                <Button {...styles.generatePdfButton} onClick={saveAndGeneratePdf}>
                                    {translate("Generate PDF")}
                                </Button>
                                {allowSendPdfViaEmail && (
                                    <Button
                                        {...styles.emailPdfButton}
                                        onClick={() => {
                                            if (validateFields()) {
                                                setSendByEmail(true);
                                            }
                                        }}
                                    >
                                        {translate("Email PDF")}
                                    </Button>
                                )}
                                <Button disabled={isSaving} {...styles.savePdfButton} onClick={savePdf}>
                                    {translate("Save")}
                                </Button>
                            </GridItem>
                        </GridContainer>
                    </LoadingOverlay>
                </ManagedAccordionSection>
            </Accordion>
            <Modal
                size={500}
                headline={translate("Your PDF is ready")}
                isOpen={isPdfReady}
                handleClose={() => setIsPdfReady(false)}
            >
                <StyledWrapper {...styles.viewPdfWrapper}>
                    <Typography {...styles.viewPdfText}>{translate("Your PDF generation is done!")}</Typography>
                    <Button onClick={() => window.open(pdfLink, "_blank")} {...styles.pdfButton}>
                        {translate("View PDF")}
                    </Button>
                </StyledWrapper>
            </Modal>
            <Modal
                size={500}
                headline={translate("Send PDF by Email")}
                isOpen={sendByEmail}
                handleClose={() => {
                    setSendByEmail(false);
                    setSendToEmail("");
                }}
            >
                <StyledWrapper {...styles.viewPdfWrapper}>
                    <TextField
                        {...styles.sendToTextField}
                        type="email"
                        min={0}
                        value={sendToEmail}
                        onChange={handleSendToEmailChange}
                        maxLength={120}
                        label={translate("Email")}
                        error={sendToEmailError}
                    />
                    <Button onClick={sendPdfByEmail} {...styles.pdfButton}>
                        {translate("Send")}
                    </Button>
                </StyledWrapper>
            </Modal>
        </>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyCustomQuoteDetailsPdfSettings),
    definition: {
        group: "Custom Quote Details" as any,
        displayName: "PDF Settings",
        icon: "Printer",
        allowedContexts: ["CustomQuoteDetailsPage" as any],
    },
};

export default widgetModule;
